import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {ISheetLocation} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';

export type RowIndex = number;
export type ColumnIndex = number;
export type SlotLocationKey = string;
export type SlotGlobalKey = string;

export class SlotLocation
{
    @serializable public readonly colIndex:ColumnIndex;
    @serializable public readonly rowIndex:RowIndex;

    public constructor(colIndex:ColumnIndex, rowIndex:RowIndex)
    {
        this.colIndex = colIndex;
        this.rowIndex = rowIndex;
    }

    public toKey():SlotLocationKey
    {
        return SlotLocation.toKey(this);
    }

    public toGlobalKey(sheetId:SheetId):SlotGlobalKey
    {
        return `${sheetId}_${this.toKey()}`;
    }

    public clone():SlotLocation
    {
        return new SlotLocation(this.colIndex, this.rowIndex);
    }

    public isEqualTo(other:SlotLocation):boolean
    {
        return other &&
            other.rowIndex == this.rowIndex &&
            other.colIndex === this.colIndex;
    }

    public translate(colDelta:number, rowDelta:number):SlotLocation
    {
        return new SlotLocation(
            this.colIndex + colDelta,
            this.rowIndex + rowDelta
        );
    }

    public static fromSheetAPI(location:ISheetLocation):SlotLocation
    {
        return new SlotLocation(location.columnIndex, location.rowIndex);
    }

    public toA1String():string
    {
        return `${MeshLocationUtils.getLetterByColumn(this.colIndex + 1)}${this.rowIndex >= 0 ? this.rowIndex + 1 : ''}`;
    }

    toString():string
    {
        return this.toDisplayString();
    }

    toDisplayString():string
    {
        return this.colIndex + ',' + this.rowIndex;
    }

    public static toKey(location:SlotLocation):SlotLocationKey
    {
        return `${location.colIndex}_${location.rowIndex}`;
    }

    public static fromKey(key:SlotLocationKey):SlotLocation
    {
        const [colIndex, rowIndex] = key.split('_').map(num => parseInt(num, 10));

        return new SlotLocation(colIndex, rowIndex);
    }

    public static fromGlobalKey(key:SlotGlobalKey):ISheetLocation
    {
        const RE_KEY = /([^_]+)_(\d+)_(\d+)/;

        const result = key.match(RE_KEY) || [];
        if( result.length == 4 )
        {
            const sheetId = result[1];
            const colIndex = result[2];
            const rowIndex = result[3];

            return {
                sheetId: sheetId,
                columnIndex: parseInt(colIndex),
                rowIndex: parseInt(rowIndex)
            } as ISheetLocation;
        }

        return {
            sheetId: '',
            columnIndex: -1,
            rowIndex: -1
        } as ISheetLocation;
    }
}
